import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { useState } from 'react';
import { first, sortBy } from 'lodash';
import { TestId } from '@components/test-ids';
import { ExpandableThemeProps, Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TourAvailability } from '@model/tours';
import { Collapse, Grid } from '@mui/material';
import { Icon } from '@components/common/generic-icon';
import { Amount } from '@model/common';
import { TourPricingRow } from './TourPricingRow';
import { mq } from '@styles/breakpoints';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { useDispatch } from 'react-redux';
import { ModalType, showModal } from '@state/modal/modalOperations';
import { DropdownOption } from '@components/common/dropdown';
import { PrivateTourModalData } from '@components/modal/private-tours/PrivateTourEnquiryModal';
import { Icons } from '@model/common/icons';
import { TripTypes } from '@model/contentful';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TourPricingMonthProps {
  theme?: Theme;
  availabilityMonth: Array<TourAvailability>;
  year: string;
  month: string;
  onSubmit: (payload: TourAvailability) => void;
  testId?: string;
  tripType?: TripTypes;
  tourName: string;
  privateTourDateOptions: Array<DropdownOption>;
  isDisabled?: boolean;
  isDefault: boolean;
  totalGuests?: number;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container: any = styled.div(({ theme }: ThemeProps) => ({
  borderTop: `1px solid ${theme.custom.colors.group10.light}`,

  ['&:last-of-type']: {
    borderBottom: `1px solid ${theme.custom.colors.group10.light}`
  }
}));

const MonthRow: any = styled(Grid)({
  cursor: 'pointer'
});

interface ColumnLabelProps extends ThemeProps {
  isSoldOut: boolean;
}

const ColumnLabel: any = styled.div(({ theme, isSoldOut }: ColumnLabelProps) => ({
  ...(theme.custom.typography.paragraph as any),
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  padding: theme.custom.spacing.large,
  ...(isSoldOut && {
    textDecoration: 'line-through'
  })
}));

const Chevron: any = styled.div(({ theme, isExpanded }: ExpandableThemeProps) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '100%',

  [mq.medium]: {
    justifyContent: 'center'
  },

  ['i']: {
    transition: `transform ${theme.custom.transitions.longSmooth}`,
    transform: `rotate(${isExpanded ? '180deg' : '0deg'})`,
    color: theme.custom.colors.group4.base,
    fontSize: '1.5rem'
  }
}));

const AvailableDatesColumn: any = styled(Grid)({
  display: 'none',

  [mq.medium]: {
    display: 'block'
  }
});

const PrivateTourEnquiryColumn: any = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
});

const PrivateTourPriceColumn: any = styled(Grid)({
  [ColumnLabel]: {
    justifyContent: 'flex-end'
  },

  [mq.medium]: {
    [ColumnLabel]: {
      justifyContent: 'flex-start'
    }
  }
});

const EnquiryButton: any = styled(Button)(({ theme }: ThemeProps) => ({
  marginLeft: theme.custom.spacing.large,
  marginRight: theme.custom.spacing.large,
  marginBottom: theme.custom.spacing.large,

  [mq.medium]: {
    marginBottom: 0
  }
}));

/* ******************************* *
 *    TourPricingMonthComponent    *
 * ******************************* */

export const TourPricingMonthComponent = ({
  month,
  year,
  availabilityMonth,
  onSubmit,
  tripType,
  testId,
  tourName,
  privateTourDateOptions,
  isDisabled,
  isDefault,
  totalGuests
}: TourPricingMonthProps) => {
  const t: any = useI18NextContext();
  const dispatch = useDispatch();

  if (!availabilityMonth.length) {
    return null;
  }
  /* *** STATE *** */
  const [isExpanded, setIsExpanded] = useState<boolean>(isDefault);

  /* *** LOGIC *** */
  const getFromPriceForMonth: () => string = () => {
    const filtered = availabilityMonth.filter((o) => !!o.price.amount);
    const cheapest = first(sortBy(filtered, (o) => o.price.amount));
    const zeroPriceLabel = tripType === TripTypes.PRIVATE_TOURS ? t('private-tours__label--enquire-for-price') : '';

    return cheapest?.price?.amount
      ? `${t('common__label--from')} ${new Amount(cheapest?.price.amount, cheapest?.price.currency.code).format()}`
      : zeroPriceLabel;
  };

  const isEverythingSoldOut: () => boolean = () =>
    !availabilityMonth.map((availability: TourAvailability) => availability.soldOut).includes(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const openEnquiryModal = () => {
    const modalData: PrivateTourModalData = {
      tourName,
      selectedDate: `${t(`common__label--${month}`)} ${year}`,
      privateTourDateOptions
    };

    dispatch(showModal(ModalType.PRIVATE_TOUR_ENQUIRY, modalData));
  };

  /* *** RENDERERS *** */
  const renderAvailabilityRows = () =>
    availabilityMonth.map((availability: TourAvailability, index: number) => (
      <TourPricingRow
        key={`${availability.departureDate}-${index}`}
        availability={availability}
        onSubmit={onSubmit}
        isDisabled={isDisabled}
        tripType={tripType}
        totalGuests={totalGuests}
      />
    ));

  const renderTourPricing = () => (
    <>
      <MonthRow container spacing={0} onClick={toggleExpand}>
        <Grid item xs={6} md={3}>
          <ColumnLabel isSoldOut={isEverythingSoldOut()}>
            {t(`common__label--${month}`)} {year}
          </ColumnLabel>
        </Grid>
        <Grid item xs={5} md={5}>
          <ColumnLabel isSoldOut={isEverythingSoldOut()}>{getFromPriceForMonth()}</ColumnLabel>
        </Grid>
        <AvailableDatesColumn item xs={12} md={3}>
          <ColumnLabel isSoldOut={isEverythingSoldOut()}>
            {isEverythingSoldOut()
              ? t('tour-product__label--sold-out')
              : `${availabilityMonth.length} ${t(
                  availabilityMonth.length === 1
                    ? 'tour-product__pricing-column--date'
                    : 'tour-product__pricing-column--dates'
                )}`}
          </ColumnLabel>
        </AvailableDatesColumn>
        <Grid item xs={1} md={1}>
          <Chevron isExpanded={isExpanded}>
            <Icon name={Icons.CHEVRON_DOWN} />
          </Chevron>
        </Grid>
      </MonthRow>
      <Collapse in={isExpanded}>{renderAvailabilityRows()}</Collapse>
    </>
  );

  const renderPrivateTourPricing = () => (
    <Grid container spacing={0}>
      <Grid item xs={6} md={4}>
        <ColumnLabel>
          {t(`common__label--${month}`)} {year}
        </ColumnLabel>
      </Grid>
      <PrivateTourPriceColumn item xs={6} md={5}>
        <ColumnLabel>{getFromPriceForMonth()}</ColumnLabel>
      </PrivateTourPriceColumn>
      <PrivateTourEnquiryColumn item xs={12} md={3}>
        <EnquiryButton
          variant={ButtonVariant.CONTAINED}
          color={ButtonColor.SECONDARY}
          label={'private-tours-priving__button-label--enquire-now'}
          size={ButtonSize.SMALL}
          onClick={openEnquiryModal}
        />
      </PrivateTourEnquiryColumn>
    </Grid>
  );

  return (
    <Container data-testid={testId || TestId.tourPricing.expandableAvailabilityMonth}>
      {tripType === TripTypes.PRIVATE_TOURS ? renderPrivateTourPricing() : renderTourPricing()}
    </Container>
  );
};

export const TourPricingMonth = withTheme(TourPricingMonthComponent);
